import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { normalizeDigits } from '../GeneralProfile/useGeneralProfileFlow';
import { DONE_STEP_ID, fetchBankAccountDetailsProgress, upsertBankAccountDetailsProgress } from './bankAccountDetailsApi';
import type { BankAccountFormValues, BankTypeCode, AccountTypeCode } from './bankAccountDetailsTypes';
import type { BankAccountDetailsStep } from './bankAccountDetailsSteps';
import { formatAccountType, formatBankType, formatYesNo } from './bankAccountDetailsDisplay';

export type BankAccountCommittedEntry = {
  stepIndex: number;
  stepId: BankAccountDetailsStep;
  fieldName: keyof BankAccountFormValues;
  displayText: string;
  rawValue: string;
};

type StepDef = {
  stepId: BankAccountDetailsStep;
  fieldName: keyof BankAccountFormValues;
  kind: 'choice' | 'text';
  required: boolean;
};

const NEXT_ASSISTANT_QUESTION_DELAY_MS = 520;

function cursorStepIdForSave(steps: StepDef[], stepIndex: number): string | null {
  if (stepIndex >= steps.length) return DONE_STEP_ID;
  return steps[stepIndex]?.stepId ?? null;
}

function buildSteps(values: BankAccountFormValues): StepDef[] {
  const consent = values.consentGiven?.trim().toLowerCase();
  if (consent === 'false' || consent === 'no') {
    return [{ stepId: 'consentGiven', fieldName: 'consentGiven', kind: 'choice', required: true }];
  }

  const bankType = values.bankType;
  const includeOther = bankType === 'other';
  const steps: StepDef[] = [
    { stepId: 'consentGiven', fieldName: 'consentGiven', kind: 'choice', required: true },
    { stepId: 'bankType', fieldName: 'bankType', kind: 'choice', required: true },
  ];
  if (includeOther) {
    steps.push({ stepId: 'bankTypeOther', fieldName: 'bankTypeOther', kind: 'text', required: true });
  }
  steps.push(
    { stepId: 'bankName', fieldName: 'bankName', kind: 'text', required: true },
    { stepId: 'branchName', fieldName: 'branchName', kind: 'text', required: true },
    { stepId: 'accountType', fieldName: 'accountType', kind: 'choice', required: true },
    { stepId: 'accNum', fieldName: 'accNum', kind: 'text', required: true },
    { stepId: 'nameKana', fieldName: 'nameKana', kind: 'text', required: true }
  );
  return steps;
}

function isConsentYes(raw: string): boolean {
  const v = raw.trim().toLowerCase();
  return v === 'yes' || v === 'true';
}

function isConsentNo(raw: string): boolean {
  const v = raw.trim().toLowerCase();
  return v === 'no' || v === 'false';
}

function formatCommitted(stepId: BankAccountDetailsStep, t: (k: string) => string, raw: string, values: BankAccountFormValues) {
  if (stepId === 'consentGiven') return formatYesNo(t, raw);
  if (stepId === 'bankType') return formatBankType(raw);
  if (stepId === 'accountType') return formatAccountType(raw);
  if (stepId === 'bankTypeOther' && values.bankType === 'other') return raw.trim();
  if (stepId === 'bankName') {
    const name = raw.trim();
    if (!name) return '';
    const typeCode = (values.bankType ?? '').trim().toLowerCase() as BankTypeCode | '';
    const typeLabel =
      typeCode === 'other'
        ? (values.bankTypeOther?.trim() || formatBankType('other'))
        : typeCode
          ? formatBankType(typeCode)
          : '';
    return typeLabel ? `${name} ${typeLabel}` : name;
  }
  return raw.trim();
}

function refreshBankNameDisplay(
  committed: BankAccountCommittedEntry[],
  t: (k: string) => string,
  values: BankAccountFormValues
): BankAccountCommittedEntry[] {
  return committed.map((c) => {
    if (c.stepId !== 'bankName') return c;
    return {
      ...c,
      displayText: formatCommitted('bankName', t, c.rawValue, values) || c.rawValue,
    };
  });
}

function findStepIndex(steps: StepDef[], stepId: BankAccountDetailsStep): number {
  return steps.findIndex((s) => s.stepId === stepId);
}

function upsertCommittedEntry(
  committed: BankAccountCommittedEntry[],
  entry: BankAccountCommittedEntry
): BankAccountCommittedEntry[] {
  const next = [...committed];
  const existingIdx = next.findIndex((c) => c.stepId === entry.stepId);
  if (existingIdx >= 0) {
    next[existingIdx] = entry;
    return next;
  }
  const bankTypeIdx = next.findIndex((c) => c.stepId === 'bankType');
  if (entry.stepId === 'bankTypeOther' && bankTypeIdx >= 0) {
    next.splice(bankTypeIdx + 1, 0, entry);
    return next;
  }
  next.push(entry);
  return next;
}

function rebuildCommitted(
  steps: StepDef[],
  values: BankAccountFormValues,
  currentStepId: string | null | undefined,
  t: (k: string) => string
): { stepIndex: number; committed: BankAccountCommittedEntry[]; isComplete: boolean } {
  const stepId = currentStepId ?? null;
  if (stepId === DONE_STEP_ID) {
    const committed: BankAccountCommittedEntry[] = steps.map((s, i) => {
      const raw = String(values[s.fieldName] ?? '').trim();
      return {
        stepIndex: i,
        stepId: s.stepId,
        fieldName: s.fieldName,
        rawValue: raw,
        displayText: formatCommitted(s.stepId, t, raw, values) || raw,
      };
    });
    return { stepIndex: steps.length, committed, isComplete: true };
  }
  const idx = stepId ? steps.findIndex((s) => s.stepId === stepId) : -1;
  const stepIndex = idx >= 0 ? idx : 0;
  const committed: BankAccountCommittedEntry[] = [];
  for (let i = 0; i < stepIndex; i++) {
    const s = steps[i];
    const raw = String(values[s.fieldName] ?? '').trim();
    committed.push({
      stepIndex: i,
      stepId: s.stepId,
      fieldName: s.fieldName,
      rawValue: raw,
      displayText: formatCommitted(s.stepId, t, raw, values) || raw,
    });
  }
  return { stepIndex, committed, isComplete: false };
}

export function useBankAccountDetailsFlow(currentEmail: string | null | undefined, t: (k: string) => string) {
  const [values, setValues] = useState<BankAccountFormValues>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [committed, setCommitted] = useState<BankAccountCommittedEntry[]>([]);
  const [editingCommitIndex, setEditingCommitIndex] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [consentDeclined, setConsentDeclined] = useState(false);
  const progressLoadedRef = useRef(false);

  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const assistantHoldTimerRef = useRef<number | null>(null);
  const [suppressNextAssistantBubble, setSuppressNextAssistantBubble] = useState(false);

  const steps = useMemo(() => buildSteps(values), [values]);

  const currentStep: StepDef | undefined = useMemo(() => {
    if (!steps.length) return undefined;
    if (editingCommitIndex !== null) return steps[editingCommitIndex];
    if (stepIndex < steps.length) return steps[stepIndex];
    return undefined;
  }, [steps, stepIndex, editingCommitIndex]);

  const isComplete =
    !consentDeclined && steps.length > 0 && stepIndex >= steps.length && editingCommitIndex === null;

  const clearAssistantHold = useCallback(() => {
    if (assistantHoldTimerRef.current != null) {
      window.clearTimeout(assistantHoldTimerRef.current);
      assistantHoldTimerRef.current = null;
    }
    setSuppressNextAssistantBubble(false);
  }, []);

  useEffect(() => {
    return () => clearAssistantHold();
  }, [clearAssistantHold]);

  const persistSnapshot = useCallback(
    async (nextValues: BankAccountFormValues, nextStepIndex: number, stepsSnapshot: StepDef[]): Promise<boolean> => {
      const email = currentEmail?.trim();
      if (!email) return true;
      const currentStepId = cursorStepIdForSave(stepsSnapshot, nextStepIndex);
      const ok = await upsertBankAccountDetailsProgress({
        email,
        values: nextValues,
        currentStepId,
      });
      if (!ok) {
        setSaveError(t('submitDocuments.bankAccountDetails.saveFailed'));
      } else {
        setSaveError(null);
      }
      return ok;
    },
    [currentEmail, t]
  );

  const handleConsentDeclined = useCallback(
    async (consentEntry: BankAccountCommittedEntry): Promise<boolean> => {
      const email = currentEmail?.trim();
      if (!email) return true;
      const ok = await upsertBankAccountDetailsProgress({
        email,
        values: { consentGiven: 'false' },
        currentStepId: null,
      });
      if (!ok) {
        setSaveError(t('submitDocuments.bankAccountDetails.saveFailed'));
        return false;
      }
      setSaveError(null);
      clearAssistantHold();
      setConsentDeclined(true);
      setValues({ consentGiven: 'false' });
      setCommitted([consentEntry]);
      setStepIndex(1);
      setEditingCommitIndex(null);
      return true;
    },
    [currentEmail, t, clearAssistantHold]
  );

  useEffect(() => {
    if (!currentEmail?.trim() || progressLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const data = await fetchBankAccountDetailsProgress(currentEmail);
        if (cancelled || !data) return;
        progressLoadedRef.current = true;
        const loadedValues = data.values ?? {};
        setValues(loadedValues);
        const built = rebuildCommitted(buildSteps(loadedValues), loadedValues, data.currentStepId, t);
        setCommitted(built.committed);
        setStepIndex(built.stepIndex);
      } catch {
        progressLoadedRef.current = true;
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentEmail, t]);

  const setField = useCallback((fieldName: keyof BankAccountFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const currentValue = currentStep ? String(values[currentStep.fieldName] ?? '') : '';

  const canSubmitCurrent = useMemo(() => {
    if (!currentStep) return false;
    const raw = String(values[currentStep.fieldName] ?? '').trim();
    return currentStep.required ? raw.length > 0 : true;
  }, [values, currentStep]);

  const advance = useCallback(async (overrideRaw?: string) => {
    if (!currentStep) return;
    const raw = (overrideRaw ?? String(values[currentStep.fieldName] ?? '')).trim();
    if (currentStep.required && !raw) return;

    // Normalize certain inputs before commit
    let committedRaw = raw;
    if (currentStep.stepId === 'accNum') {
      committedRaw = normalizeDigits(raw).replace(/[^\d]/g, '').slice(0, 7);
    }

    const entry: BankAccountCommittedEntry = {
      stepIndex: editingCommitIndex ?? stepIndex,
      stepId: currentStep.stepId,
      fieldName: currentStep.fieldName,
      rawValue: committedRaw,
      displayText: formatCommitted(currentStep.stepId, t, committedRaw, values) || committedRaw,
    };

    // Apply bankType switching rule: if bankType is not other, clear bankTypeOther
    let nextValues: BankAccountFormValues = { ...values, [currentStep.fieldName]: committedRaw };
    if (currentStep.stepId === 'bankType') {
      const v = committedRaw.toLowerCase() as BankTypeCode;
      const prevType = values.bankType?.trim().toLowerCase();
      nextValues.bankType = v;
      if (v !== 'other') {
        delete nextValues.bankTypeOther;
      } else if (prevType !== 'other') {
        delete nextValues.bankTypeOther;
      }
    }
    if (currentStep.stepId === 'accountType') {
      nextValues.accountType = committedRaw.toLowerCase() as AccountTypeCode;
    }
    if (currentStep.stepId === 'consentGiven') {
      const v = committedRaw.toLowerCase();
      nextValues.consentGiven = (v === 'yes' || v === 'true' ? 'true' : v === 'no' || v === 'false' ? 'false' : undefined) as
        | 'true'
        | 'false'
        | undefined;
    }

    entry.displayText =
      formatCommitted(currentStep.stepId, t, committedRaw, nextValues) || committedRaw;

    setValues(nextValues);

    if (currentStep.stepId === 'consentGiven' && isConsentNo(committedRaw)) {
      await handleConsentDeclined(entry);
      return;
    }

    if (editingCommitIndex !== null) {

      // Case 3: standard type → その他 — collect bankTypeOther before returning to review.
      if (currentStep.stepId === 'bankType' && nextValues.bankType === 'other') {
        const wasOther = values.bankType?.trim().toLowerCase() === 'other';
        if (!wasOther) {
          const nextSteps = buildSteps(nextValues);
          const otherStepIdx = findStepIndex(nextSteps, 'bankTypeOther');
          setCommitted((prev) => {
            const next = upsertCommittedEntry(prev, entry);
            return next.filter((c) => c.stepId !== 'bankTypeOther');
          });
          if (otherStepIdx >= 0) {
            setStepIndex(nextSteps.length);
            setEditingCommitIndex(otherStepIdx);
            await persistSnapshot(nextValues, otherStepIdx, nextSteps);
          }
          return;
        }
      }

      setCommitted((prev) => {
        let next = upsertCommittedEntry(prev, entry);
        if (entry.stepId === 'bankType' && nextValues.bankType !== 'other') {
          next = next.filter((c) => c.stepId !== 'bankTypeOther');
        }
        if (entry.stepId === 'bankType' || entry.stepId === 'bankTypeOther') {
          next = refreshBankNameDisplay(next, t, nextValues);
        }
        return next;
      });
      const finalSteps = buildSteps(nextValues);
      setStepIndex(finalSteps.length);
      setEditingCommitIndex(null);
      await persistSnapshot(nextValues, finalSteps.length, finalSteps);
      return;
    }

    clearAssistantHold();
    setSuppressNextAssistantBubble(true);
    assistantHoldTimerRef.current = window.setTimeout(() => {
      assistantHoldTimerRef.current = null;
      setSuppressNextAssistantBubble(false);
    }, NEXT_ASSISTANT_QUESTION_DELAY_MS);

    setCommitted((prev) => {
      const nextCommitted = [...prev, entry];
      // If bankType is non-other, ensure no bankTypeOther commit exists.
      let filtered =
        nextValues.bankType !== 'other' ? nextCommitted.filter((c) => c.stepId !== 'bankTypeOther') : nextCommitted;
      if (entry.stepId === 'bankType' || entry.stepId === 'bankTypeOther') {
        filtered = refreshBankNameDisplay(filtered, t, nextValues);
      }
      const nextStepIndex = filtered.length;
      void persistSnapshot(nextValues, nextStepIndex, buildSteps(nextValues));
      return filtered;
    });
    setStepIndex((i) => i + 1);
  }, [currentStep, values, editingCommitIndex, stepIndex, t, persistSnapshot, clearAssistantHold, steps.length, handleConsentDeclined]);

  const undo = useCallback(() => {
    clearAssistantHold();
    setCommitted((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const nextStepIndex = prev.length - 1;
      setValues((v) => {
        const next = { ...v };
        delete (next as any)[last.fieldName];
        void persistSnapshot(next, nextStepIndex, buildSteps(next));
        return next;
      });
      setStepIndex(nextStepIndex);
      return prev.slice(0, -1);
    });
  }, [clearAssistantHold, persistSnapshot]);

  const showEditForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (!isComplete || committed.length === 0) return false;
      return commitIndex >= 0 && commitIndex < committed.length;
    },
    [editingCommitIndex, isComplete, committed.length]
  );

  const jumpToEditCommitIndex = useCallback(
    (commitIndex: number) => {
      const fullyComplete = stepIndex >= steps.length;
      if (!fullyComplete || commitIndex < 0 || commitIndex >= committed.length) return;

      const entry = committed[commitIndex];
      if (entry.stepId === 'consentGiven' && isConsentYes(entry.rawValue)) {
        const confirmed = window.confirm(t('submitDocuments.bankAccountDetails.consentEditConfirm'));
        if (!confirmed) return;
      }

      const stepIdx = steps.findIndex((s) => s.stepId === entry.stepId);
      if (stepIdx < 0) return;

      clearAssistantHold();
      setEditingCommitIndex(stepIdx);
    },
    [stepIndex, steps, committed, clearAssistantHold, t]
  );

  const showUndoForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (isComplete && committed.length > 0) return commitIndex === committed.length - 1;
      return stepIndex === commitIndex + 1;
    },
    [editingCommitIndex, isComplete, committed.length, stepIndex]
  );

  const handleFinalComplete = useCallback(async (): Promise<boolean> => {
    const email = currentEmail?.trim();
    if (!email) return true;
    const ok = await upsertBankAccountDetailsProgress({
      email,
      values: { ...values },
      currentStepId: DONE_STEP_ID,
    });
    if (!ok) {
      setSaveError(t('submitDocuments.bankAccountDetails.saveFailed'));
      return false;
    }
    return true;
  }, [currentEmail, values, t]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [committed, stepIndex, isComplete, consentDeclined, suppressNextAssistantBubble]);

  return {
    t,
    steps,
    currentStep,
    currentValue,
    canSubmitCurrent,
    setField,
    advance,
    undo,
    committed,
    isComplete,
    consentDeclined,
    editingCommitIndex,
    showEditForCommitIndex,
    jumpToEditCommitIndex,
    showUndoForCommitIndex,
    threadEndRef,
    assistantQuestionVisible: !suppressNextAssistantBubble,
    isLoading,
    saveError,
    values,
    handleFinalComplete,
  };
}

